import { VStack, Section } from 'tio-ui/components/layout';
import Text from 'tio-reimagined/components/text';
import Select from 'tio-reimagined/components/select';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import type Owner from '@ember/owner';
import type { IntlService } from 'ember-intl';
import { t } from 'ember-intl';
import SecuritySettings from 'tio-employee/components/app/profile/my-profile/security-settings';
import FamilySection from 'tio-employee/components/app/profile/my-profile/family-section';
import type ProfileController from 'tio-employee/controllers/authenticated/profile';
import type EmployeeModel from 'tio-common/models/employee';
import type PersonModel from 'tio-common/models/person';

// Supported locales - must match application.ts
const SUPPORTED_LOCALES = ['en-us', 'fr-ca', 'es-mx'] as const;
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
type LocaleKey = SupportedLocale | 'auto';

interface LocaleOption {
  key: LocaleKey;
  label: string;
}

const LOCALE_OPTIONS: LocaleOption[] = [
  { key: 'auto', label: 'Auto-detect (browser)' },
  { key: 'en-us', label: 'English (US)' },
  { key: 'fr-ca', label: 'Français (Canada)' },
  { key: 'es-mx', label: 'Español' },
];

interface S {
  Args: {
    controller: ProfileController;
    model: {
      employee?: EmployeeModel;
      familyPerson?: PersonModel;
    };
  };
}

export default class ProfileRoute extends Component<S> {
  @service declare intl: IntlService;
  @tracked selectedLocale: LocaleKey;

  constructor(owner: Owner, args: S['Args']) {
    super(owner, args);

    // Initialize from localStorage
    const savedLocale = localStorage.getItem('locale');
    this.selectedLocale =
      savedLocale && SUPPORTED_LOCALES.includes(savedLocale as SupportedLocale)
        ? (savedLocale as SupportedLocale)
        : 'auto';
  }

  get localeOptions() {
    return LOCALE_OPTIONS;
  }

  get showFamilySection() {
    // Only show if company has allowFamilyPlan enabled
    return this.args.model.employee?.company?.companySetting?.allowFamilyPlan === true;
  }

  handleLocaleChange = (selectedKey: string) => {
    const key = selectedKey as LocaleKey;
    this.selectedLocale = key;

    if (key === 'auto') {
      localStorage.removeItem('locale');
    } else {
      localStorage.setItem('locale', key);
    }

    // Reload to apply new locale
    window.location.reload();
  };

  <template>
    <VStack class="w-full">
      <SecuritySettings @familyPerson={{@model.familyPerson}} @employee={{@model.employee}} />
      {{#if @model.employee}}
        {{#if this.showFamilySection}}
          <FamilySection @employee={{@model.employee}} />
        {{/if}}
      {{/if}}

      <Section>
        <:header>{{t "profile.language.title"}}</:header>
        <:body>
          <VStack class="gap-3">
            <Text @variant="muted" @size="sm">{{t "profile.language.description"}}</Text>
            <Select
              @items={{this.localeOptions}}
              @selected={{this.selectedLocale}}
              @onChange={{this.handleLocaleChange}}
              class="w-full max-w-xs"
            />
          </VStack>
        </:body>
      </Section>
    </VStack>
  </template>
}
